import { createSlice } from '@reduxjs/toolkit';

import { type CustomRatesConfig } from '../api';

import type { PayloadAction } from '@reduxjs/toolkit';

export interface DirectRates {
	rate_plan_type: 'TIERED' | 'IC_PLUS' | 'FLAT' | 'ALL';
	pricing_mode: {
		discount_rate_mode: 'same_for_all' | 'different_by_card_type';
		authorization_fee_mode: 'same_for_all' | 'different_by_transaction_type';
		pass_through_cost_mode: 'standard' | 'custom';
	};
	rates: {
		standard_rates: {
			mode: 'same_for_all' | 'different_by_card_type';
			by_card_type: Record<
				string,
				{
					discount_rate: number | null;
					authorization_fee: number;
				}
			>;
		};
		tiered_rates: {
			mode: 'same_for_all' | 'different_by_card_type';
			by_card_type?: Record<string, any>;
			same_for_all?: {
				credit_rates: {
					qualified_rate: number;
					qualified_per_item: number;
					midqualified_rate: number;
					midqualified_per_item: number;
					nonqualified_rate: number;
					nonqualified_per_item: number;
				};
				debit_rates: {
					qualified_rate: number;
					qualified_per_item: number;
					midqualified_rate: number;
					midqualified_per_item: number;
					nonqualified_rate: number;
					nonqualified_per_item: number;
				};
				same_as_credit: boolean;
			};
		};
	};
	pass_through_costs: {
		use_custom_assessment_rates: boolean;
		custom_assessment_rate: number | null;
		use_bundled_amex_rate: boolean;
		bundled_amex_rate: number | null;
	};
}

export interface ConfigState {
	configs: CustomRatesConfig[];
	hasConfigs: boolean;
	isLoading: boolean;
	error: string | null;
	directRates: DirectRates | null;
}

const initialState: ConfigState = {
	configs: [],
	hasConfigs: false,
	isLoading: false,
	error: null,
	directRates: null,
};

const configSlice = createSlice({
	name: 'config',
	initialState,
	reducers: {
		setConfigs: (state, action: PayloadAction<CustomRatesConfig[]>) => {
			state.configs = action.payload;
			state.hasConfigs = action.payload.length > 0;
		},
		setHasConfigs: (state, action: PayloadAction<boolean>) => {
			state.hasConfigs = action.payload;
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		},
		setError: (state, action: PayloadAction<string | null>) => {
			state.error = action.payload;
		},
		clearConfigs: (state) => {
			state.configs = [];
			state.hasConfigs = false;
			state.isLoading = false;
			state.error = null;
		},
		setDirectRates: (state, action: PayloadAction<DirectRates | null>) => {
			state.directRates = action.payload;
		},
		clearDirectRates: (state) => {
			state.directRates = null;
		},
	},
});

export const {
	setConfigs,
	setHasConfigs,
	setLoading,
	setError,
	clearConfigs,
	setDirectRates,
	clearDirectRates,
} = configSlice.actions;

export default configSlice.reducer;
